import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/authStore.js';
import { usePendingQueueStore } from './pendingQueueStore.js';
import { completeLessonDirect, invalidateProgress } from './hooks.js';
import {
  useQueueFlusher,
  drainEntries,
  type FlushController,
} from '../lib/useQueueFlusher.js';

// ~2.5 min @ 20s, then we give up. Lesson-only rule: `complete_lesson` is
// NOT replay-idempotent (the known XP-inflation replay class — see
// DECISIONS.md), so unlike the flashcard/practice queues this one must not
// retry forever.
const MAX_ATTEMPTS = 8;

/**
 * Mount once at the top of the authed tree. Watches the offline queue +
 * online/focus events and flushes entries to Supabase when possible.
 *
 * Keeps retry logic out of the LessonPlayer so a crash mid-completion
 * still results in an eventually-consistent write.
 */
export function PendingQueueFlusher() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const items = usePendingQueueStore((s) => s.items);
  const remove = usePendingQueueStore((s) => s.remove);
  const markAttempted = usePendingQueueStore((s) => s.markAttempted);

  const flush = useCallback(
    async (ctl: FlushController) => {
      if (!user) return;
      const mine = items.filter(
        (i) => i.userId === user.id && (i.attempts ?? 0) < MAX_ATTEMPTS,
      );
      await drainEntries({
        entries: mine,
        send: (entry) => completeLessonDirect(entry.payload),
        onSent: (entry) => {
          remove('items', [entry.id]);
          invalidateProgress(qc, user.id);
        },
        onFailed: (entry) => markAttempted('items', [entry.id]),
        cancelled: ctl.cancelled,
      });
    },
    [user, items, remove, markAttempted, qc],
  );

  useQueueFlusher(user ? flush : null);
  return null;
}
